import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'

import { ConversationState } from '@/composition/conversations'
import { UserState } from '@/composition/users'
import config from '@/config'
import { useGlobalStore } from '@/store/app'
import { getTokenV2 } from '@ghl/clientportal-core'
import { isAuthV2Enabled, isGokollab } from '@ghl/clientportal-core/init'
import Cookies from 'js-cookie'
import { io } from 'socket.io-client'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'
import restAgent from './restAgent'

// Keep token-related functions separate as they're utility functions
export async function getIAMV2Token(): Promise<string | undefined> {
  const token = await getTokenV2()
  if (token) {
    return token
  }

  if (!isAuthV2Enabled() || isGokollab()) {
    const legacyToken = Cookies.get('cat-v2') || window.localStorage.getItem('cat-v2')
    if (legacyToken) return legacyToken
    return await generateIAMV2Token()
  }

  return undefined
}

async function generateIAMV2Token(): Promise<string | undefined> {
  try {
    const response = await restAgent.ClientPortal.generateIamV2Token()
    const keyToken = response.data.token
    if (keyToken) {
      await Cookies.set('cat-v2', keyToken, {
        expires: 1,
        sameSite: 'None',
        secure: true,
      })
    }
    return keyToken
  } catch (error) {
    console.error(error)
  }
}

@ServiceWithStoreDependencies('locationId')
class SocketsServiceCls {
  private messageQueue: any[] = []

  constructor() {}

  private get locationId(): string {
    const appStore = useGlobalStore()
    return appStore.locationId
  }

  async initializeSocketService(
    onMessageCallback: (message: any) => void,
    socketConnectionCallback: (state: boolean) => void
  ): Promise<void> {
    try {
      const iamV2Token = await getIAMV2Token()

      ConversationState.state.socket = io(config.COMMUNITIES_SOCKETS_SERVER_URL, {
        path: '/sockets-communities/socket.io',
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        query: {
          authorization: isGokollab() ? iamV2Token : 'Bearer ' + iamV2Token,
          isGokollab: isGokollab(),
          gokollabContactId: UserState.state.currentUser?.gokollabContactId,
          channel: 'APP',
          source: 'PORTAL_USER',
          path: '/sockets-communities/socket.io',
          version: '2021-07-28',
          visitorId: '123',
          ...(isAuthV2Enabled() && !isGokollab() ? { userV2: 'true' } : {}),
        },
      })

      ConversationState.state.socket.on('message', message => {
        onMessageCallback(message)
      })
      ConversationState.state.socket.on('connectionState', message => {
        console.info('connectionState', message)
        socketConnectionCallback(true)
        this.flushMessageQueue()
      })
      ConversationState.state.socket.on('disconnect', reason => {
        socketConnectionCallback(false)
        console.info('socket disconnected', reason)
      })
      ConversationState.state.socket.on('connect_error', error => {
        console.error('Connection error:', error)
      })

      ConversationState.state.socket.io.on('reconnect_attempt', attempt => {
        console.log(`Reconnection attempt: ${attempt}`)
      })
    } catch (e) {
      console.error(e)
    }
  }

  async sendMessage(messagePayload: any): Promise<void> {
    try {
      const eventPayload = {
        event: 'sendMessage',
        payload: messagePayload,
        locationId: this.locationId,
      }
      trackCustomGAEvents(GAEventTypes.CHAT_MESSAGE_SENT, {
        senderId: messagePayload.sender,
        locationId: this.locationId,
      })
      if (ConversationState.state.socket?.connected) {
        await ConversationState.state.socket?.emit('message', eventPayload)
      } else {
        console.error('Socket is not initialized.')
        if (!this.messageQueue.some(msg => msg.payload.uniqueRefId === eventPayload.payload.uniqueRefId)) {
          trackCustomGAEvents(GAEventTypes.CHAT_MESSAGE_FAILED, {
            senderId: messagePayload.sender,
            recipientId: messagePayload.recipient,
            locationId: this.locationId,
            error: 'Socket is not initialized.',
          })
          this.messageQueue.push(eventPayload)
        }
      }
    } catch (e) {
      trackCustomGAEvents(GAEventTypes.CHAT_MESSAGE_FAILED, {
        senderId: messagePayload.sender,
        recipientId: messagePayload.recipient,
        locationId: this.locationId,
        error: e,
      })
      console.error(e)
    }
  }

  async sendConversationEvents(payload: any): Promise<void> {
    if (ConversationState.state.socket?.connected) {
      ConversationState.state.socket?.emit('message', {
        event: 'conversationEvents',
        payload: payload,
        locationId: this.locationId,
      })
    } else {
      console.error('Socket is not initialized.')
    }
  }

  private flushMessageQueue() {
    this.messageQueue.forEach(async message => {
      await ConversationState.state.socket?.emit('message', message)
    })
  }
}

// Export singleton instance
export const SocketService = new SocketsServiceCls()

// Export type for dependency injection
export type ISocketsService = SocketsServiceCls

// Export commonly used methods directly
export const { initializeSocketService, sendMessage, sendConversationEvents } = SocketService

// Composable hook for easier usage
export function useSockets() {
  return {
    initializeSocketService: SocketService.initializeSocketService.bind(SocketService),
    sendMessage: SocketService.sendMessage.bind(SocketService),
    sendConversationEvents: SocketService.sendConversationEvents.bind(SocketService),
  }
}
